import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProdiSelect from "@/components/ProdiSelect";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, KeyRound, Trash2, UserCog, UsersRound, LockOpen, ShieldOff, RotateCcw, Copy, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatWIBTime, isFuture } from "@/lib/datetime";
import { ROLE_LABEL, ROLE_BADGE_CLASS, isSuperAdmin } from "@/lib/roles";
import BulkCreateAdminDialog from "@/components/BulkCreateAdminDialog";
import {
    DaftarTabKeaktifan, KeteranganStatus, RingkasanProdiKeaktifan, SelTerakhirLogin, useTabKeaktifan,
} from "@/components/KeaktifanAkun";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { TAB_KEAKTIFAN } from "@/lib/keaktifan";
import StickyHeader from "@/components/StickyHeader";
import PasswordRequirements from "@/components/PasswordRequirements";
import { isPasswordValid } from "@/lib/passwordRules";
import {
    aksiResetAkun, bisaUbahEmail, cakupanAkun, modulBelumDipilih, modulKoasPilihan, periksaModulAkun, pesanAkunDiperbarui, prodiIdKirim,
    prodiSetelahGantiPeran, TEKS_MODUL_BELUM_DIPILIH,
} from "@/lib/akunAdmin";
import UbahEmailAkunDialog from "@/components/UbahEmailAkunDialog";
import { buatPenjagaSimpan } from "@/lib/penjagaSimpan";

const EMPTY = { email: "", nama: "", role: "admin_prodi", prodi_id: "", instalasi_ids: [], password: "" };

export default function Users() {
    const { user: me } = useAuth();
    const iAmSuper = isSuperAdmin(me?.role);
    const roleOptions = iAmSuper
        ? ["admin_prodi", "admin_koas", "admin_instalasi", "admin_ufp", "super_admin"]
        : ["admin_prodi"];

    const [users, setUsers] = useState([]);
    const [prodi, setProdi] = useState([]);
    const [instalasi, setInstalasi] = useState([]);
    const [dialog, setDialog] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [editing, setEditing] = useState(null);
    // Simpan ganda: tombol dinonaktifkan selama menyimpan + penjaga sinkron (klik kedua
    // dlm hitungan milidetik bisa terjadi sebelum React sempat menonaktifkan tombol).
    const [saving, setSaving] = useState(false);
    const jagaSimpan = useRef(buatPenjagaSimpan()).current;
    const [resetLinkFor, setResetLinkFor] = useState(null);
    const [resetLinkLoading, setResetLinkLoading] = useState(false);
    const [confirmDel, setConfirmDel] = useState(null);
    const [resetTotpFor, setResetTotpFor] = useState(null);
    // Reset kata sandi sementara (khusus Super Admin): sandi hanya disimpan di state ini
    // selama dialog hasil terbuka, lalu dibuang -- tidak pernah ditampilkan lagi.
    const [resetSandiFor, setResetSandiFor] = useState(null);
    const [resetSandiLoading, setResetSandiLoading] = useState(false);
    const [sandiSementara, setSandiSementara] = useState(null);
    const [resetTotpLoading, setResetTotpLoading] = useState(false);
    const [ubahEmailFor, setUbahEmailFor] = useState(null);
    // Indikator keaktifan (GET /admin/keaktifan, hanya-baca); dimuat terpisah supaya gagalnya tidak
    // mengganggu daftar akun.
    const [keaktifan, setKeaktifan] = useState(null);
    // Tab bawaan di halaman ini = Status Akun (tabel kelola akun spt sebelumnya); ringkasan prodi di tab kedua.
    const [tab, ubahTab] = useTabKeaktifan(TAB_KEAKTIFAN.akun);

    const load = async () => {
        try {
            const [u, p, inst] = await Promise.all([api.get("/users"), api.get("/prodi"), api.get("/instalasi-klinik")]);
            setUsers(u.data); setProdi(p.data); setInstalasi(inst.data);
        } catch { toast.error("Gagal memuat"); }
        api.get("/admin/keaktifan").then(({ data }) => setKeaktifan(data)).catch(() => setKeaktifan(null));
    };
    useEffect(() => { load(); }, []);
    const keaktifanMap = useMemo(() => Object.fromEntries((keaktifan?.akun || []).map((a) => [a.id, a])), [keaktifan]);

    const prodiMap = useMemo(() => Object.fromEntries(prodi.map((p) => [p.id, p])), [prodi]);
    const pilihanModul = useMemo(() => modulKoasPilihan(prodi), [prodi]);
    const instalasiMap = useMemo(() => Object.fromEntries(instalasi.map((i) => [i.id, i.nama])), [instalasi]);
    const isFormInstalasi = form.role === "admin_instalasi";
    const toggleInstalasi = (id, on) => setForm((f) => ({
        ...f, instalasi_ids: on ? [...f.instalasi_ids, id] : f.instalasi_ids.filter((x) => x !== id),
    }));

    const openAdd = () => { setEditing(null); setForm(EMPTY); setDialog(true); };
    const openEdit = (u) => {
        setEditing(u);
        setForm({ email: u.email, nama: u.nama, role: u.role, prodi_id: u.prodi_id || "", instalasi_ids: u.instalasi_ids || [], password: "" });
        setDialog(true);
    };

    const submit = () => jagaSimpan(async () => {
        setSaving(true);
        try { await simpan(); } finally { setSaving(false); }
    });

    const simpan = async () => {
        if (isFormInstalasi && form.instalasi_ids.length === 0) return toast.error("Pilih minimal satu instalasi");
        // instalasi_ids hanya dikirim utk Admin Instalasi (server menolaknya utk peran lain).
        const { instalasi_ids, ...dasar } = form;
        const tautan = isFormInstalasi ? { instalasi_ids } : {};
        try {
            if (editing) {
                const galatModul = periksaModulAkun(form, editing);
                if (galatModul) return toast.error(galatModul);
                const payload = { nama: form.nama, role: form.role, prodi_id: prodiIdKirim(form), ...tautan };
                const { data } = await api.put(`/users/${editing.id}`, payload);
                toast.success(pesanAkunDiperbarui(data));
            } else {
                if (form.role === "admin_prodi" && !form.prodi_id) return toast.error("Pilih prodi");
                const galatModul = periksaModulAkun(form, null);
                if (galatModul) return toast.error(galatModul);
                if (!isPasswordValid(form.password)) return toast.error("Kata sandi belum memenuhi semua syarat di bawah");
                await api.post("/users", { ...dasar, prodi_id: prodiIdKirim(form), ...tautan });
                toast.success("User dibuat. Wajib ganti kata sandi saat login pertama.");
            }
            setDialog(false); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    // Reset password SELALU lewat link email (sekali pakai, dibuat sendiri oleh
    // pemilik akun) -- tidak ada lagi password sementara yg diketik/dilihat admin.
    const sendResetLink = async () => {
        setResetLinkLoading(true);
        try {
            const { data } = await api.post(`/users/${resetLinkFor.id}/send-reset-link`);
            toast.success(data.message || `Link reset kata sandi dikirim ke ${resetLinkFor.email}`);
            setResetLinkFor(null);
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setResetLinkLoading(false);
    };

    const toggleActive = async (u) => {
        try {
            await api.put(`/users/${u.id}`, { active: !u.active });
            toast.success(u.active ? "User dinonaktifkan" : "User diaktifkan");
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const del = async () => {
        try {
            await api.delete(`/users/${confirmDel.id}`);
            toast.success("User dihapus"); setConfirmDel(null); load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    const resetSandi = async () => {
        setResetSandiLoading(true);
        try {
            const { data } = await api.post(`/users/${resetSandiFor.id}/reset-password`);
            setSandiSementara({ email: resetSandiFor.email, sandi: data.password_sementara });
            setResetSandiFor(null);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setResetSandiLoading(false);
    };

    const salinSandi = async () => {
        try { await navigator.clipboard.writeText(sandiSementara.sandi); toast.success("Disalin"); }
        catch { toast.error("Gagal menyalin. Salin manual saja."); }
    };

    const resetTotp = async () => {
        setResetTotpLoading(true);
        try {
            await api.post(`/users/${resetTotpFor.id}/totp/reset`);
            toast.success(`Authenticator ${resetTotpFor.email} direset. Wajib daftar ulang saat login berikutnya.`);
            setResetTotpFor(null);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
        setResetTotpLoading(false);
    };

    const unlock = async (u) => {
        try {
            await api.post(`/users/${u.id}/unlock`);
            toast.success(`Kunci akun ${u.email} dibuka`);
            load();
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <Tabs value={tab} onValueChange={ubahTab} className="max-w-6xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Manajemen Akses</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Akun Admin
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">Kelola akun Super Admin, Admin UFP, Admin Prodi, Admin Koas & Admin Instalasi.</p>
                </div>
                {iAmSuper && (
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="users-bulk-add-button" className="border-teal-300 text-teal-800 hover:bg-teal-50">
                            <UsersRound className="w-4 h-4 mr-2" /> Tambah Banyak Akun
                        </Button>
                        <Button onClick={openAdd} data-testid="users-add-button" className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                            <Plus className="w-4 h-4 mr-2" /> Tambah Akun
                        </Button>
                    </div>
                )}
            </div>
            <DaftarTabKeaktifan />
            </StickyHeader>

            <TabsContent value={TAB_KEAKTIFAN.akun}>
            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                <th>Nama</th><th>Email</th><th>Role</th><th>Prodi / Modul / Instalasi</th><th>Status</th><th>Terakhir login</th><th className="text-right pr-4">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} className="border-t border-slate-200 hover:bg-slate-50">
                                    <td className="font-semibold text-slate-800">{u.nama}</td>
                                    <td className="font-mono text-xs">{u.email}</td>
                                    <td>
                                        {ROLE_BADGE_CLASS[u.role]
                                            ? <Badge className={ROLE_BADGE_CLASS[u.role]}>{ROLE_LABEL[u.role]}</Badge>
                                            : <Badge variant="outline">{ROLE_LABEL[u.role] || u.role}</Badge>}
                                    </td>
                                    <td className="text-slate-600 text-xs">
                                        {cakupanAkun(u, prodiMap, instalasiMap)}
                                    </td>
                                    <td>
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            <button onClick={() => toggleActive(u)} className={`px-2 py-0.5 rounded-full text-[11px] font-semibold border ${u.active !== false ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-slate-100 text-slate-500 border-slate-300"}`}>
                                                {u.active !== false ? "Aktif" : "Nonaktif"}
                                            </button>
                                            {isFuture(u.locked_until) && (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold border bg-rose-100 text-rose-700 border-rose-300" data-testid={`user-locked-${u.id}`}>
                                                    Terkunci sampai {formatWIBTime(u.locked_until)}
                                                </span>
                                            )}
                                            {u.must_change_password && <span className="text-[10px] text-amber-600 font-semibold">Wajib ganti sandi</span>}
                                            {u.role === "super_admin" && (
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${u.totp_enabled ? "bg-teal-100 text-teal-700 border-teal-300" : "bg-amber-100 text-amber-700 border-amber-300"}`}>
                                                    {u.totp_enabled ? "TOTP Aktif" : "TOTP Belum Didaftar"}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td>{keaktifanMap[u.id] ? <SelTerakhirLogin akun={keaktifanMap[u.id]} /> : <span className="text-xs text-slate-400">-</span>}</td>
                                    <td className="text-right pr-2">
                                        {/* Admin UFP: hanya boleh lihat + kirim link reset password (ke admin_ufp/
                                            admin_prodi, tidak ke super_admin). Tambah/edit/hapus/unlock akun kini
                                            eksklusif Super Admin -- backend menolak keras juga (bukan cuma UI). */}
                                        {iAmSuper && isFuture(u.locked_until) && (
                                            <Button size="sm" variant="ghost" className="text-emerald-700" onClick={() => unlock(u)} title="Buka kunci sekarang" data-testid={`user-unlock-${u.id}`}>
                                                <LockOpen className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {iAmSuper && (
                                            <Button size="sm" variant="ghost" onClick={() => openEdit(u)} title="Edit"><UserCog className="w-4 h-4" /></Button>
                                        )}
                                        {bisaUbahEmail(me, u) && (
                                            <Button size="sm" variant="ghost" onClick={() => setUbahEmailFor(u)} title="Ubah email (perbaiki salah ketik)" aria-label={`Ubah email akun ${u.nama}`} data-testid={`user-ubah-email-${u.id}`}>
                                                <Mail className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {aksiResetAkun(me, u).sandiSementara && (
                                            <Button size="sm" variant="ghost" onClick={() => setResetSandiFor(u)} title="Reset kata sandi (kata sandi sementara)" data-testid={`user-reset-password-${u.id}`}>
                                                <RotateCcw className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {/* Link reset lewat email hanya utk akun Super Admin (hemat kuota Resend). */}
                                        {aksiResetAkun(me, u).linkEmail && (
                                            <Button size="sm" variant="ghost" onClick={() => setResetLinkFor(u)} title="Kirim link reset kata sandi" data-testid={`user-reset-link-${u.id}`}>
                                                <KeyRound className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {iAmSuper && u.role === "super_admin" && u.id !== me.id && (
                                            <Button size="sm" variant="ghost" className="text-amber-700" onClick={() => setResetTotpFor(u)} title="Reset authenticator (TOTP)" data-testid={`user-reset-totp-${u.id}`}>
                                                <ShieldOff className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {iAmSuper && (
                                            <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setConfirmDel(u)}><Trash2 className="w-4 h-4" /></Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {keaktifan && (
                    <div className="px-4 py-2 border-t bg-slate-50"><KeteranganStatus ambang={keaktifan.ambang} /></div>
                )}
            </Card>
            </TabsContent>

            <TabsContent value={TAB_KEAKTIFAN.prodi}>
                <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="users-ringkasan-prodi">
                    <div className="px-4 py-2 border-b bg-slate-50 text-xs font-semibold text-slate-600">
                        Ringkasan per Program Studi — terakhir simpan rotasi & terisi/wajib bulan ini (sama dengan Monitoring)
                    </div>
                    {keaktifan
                        ? <RingkasanProdiKeaktifan prodi={keaktifan.prodi} bulan={keaktifan.bulan} tahun={keaktifan.tahun} />
                        : <div className="p-6 text-sm text-slate-500">Ringkasan belum tersedia (memuat atau gagal dimuat).</div>}
                </Card>
            </TabsContent>

            <Dialog open={dialog} onOpenChange={setDialog}>
                <DialogContent>
                    <DialogHeader><DialogTitle>{editing ? "Edit Admin" : "Tambah Admin"}</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Nama</Label>
                            <Input value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Email</Label>
                            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Role</Label>
                            <Select
                                value={form.role}
                                onValueChange={(v) => setForm({ ...form, role: v, prodi_id: prodiSetelahGantiPeran(form, v, editing) })}
                                disabled={!iAmSuper}
                            >
                                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {[...new Set([...(editing ? [editing.role] : []), ...roleOptions])].map((r) => (
                                        <SelectItem key={r} value={r}>{ROLE_LABEL[r] || r}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {!iAmSuper && (
                                <p className="text-[11px] text-slate-500 mt-1">Admin UFP hanya dapat membuat akun Admin Prodi.</p>
                            )}
                        </div>
                        {form.role === "admin_prodi" && (
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Prodi</Label>
                                <ProdiSelect
                                    prodi={prodi}
                                    value={form.prodi_id}
                                    onValueChange={(v) => setForm({ ...form, prodi_id: v })}
                                    placeholder="Pilih prodi"
                                />
                            </div>
                        )}
                        {form.role === "admin_koas" && (
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Modul</Label>
                                <ProdiSelect
                                    prodi={pilihanModul}
                                    value={form.prodi_id}
                                    onValueChange={(v) => setForm({ ...form, prodi_id: v })}
                                    showJenjang={false}
                                    placeholder="Pilih modul"
                                    searchPlaceholder="Cari nama modul..."
                                    emptyText="Modul tidak ditemukan."
                                    testId="user-modul-select"
                                />
                                {modulBelumDipilih(form, editing) && (
                                    <p className="text-[11px] text-slate-500 mt-1" data-testid="user-modul-belum-dipilih">{TEKS_MODUL_BELUM_DIPILIH}</p>
                                )}
                            </div>
                        )}
                        {isFormInstalasi && (
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">
                                    Instalasi ({form.instalasi_ids.length} dipilih)
                                </Label>
                                <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-slate-200 p-2 space-y-1" data-testid="user-instalasi-list">
                                    {instalasi.map((i) => (
                                        <label key={i.id} className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer px-1 py-0.5">
                                            <Checkbox
                                                className="mt-0.5"
                                                checked={form.instalasi_ids.includes(i.id)}
                                                onCheckedChange={(v) => toggleInstalasi(i.id, v === true)}
                                                data-testid={`user-instalasi-${i.id}`}
                                            />
                                            <span>{i.nama}</span>
                                        </label>
                                    ))}
                                    {instalasi.length === 0 && <p className="text-xs text-slate-400">Master Instalasi kosong.</p>}
                                </div>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Hanya melihat PPDS yang Stase/Jaga di instalasi terpilih (Monitoring Rotasi Klinik, Per Instalasi).
                                </p>
                            </div>
                        )}
                        {!editing && (
                            <div>
                                <Label className="text-xs font-semibold uppercase text-slate-600">Kata Sandi Sementara</Label>
                                <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                                <PasswordRequirements password={form.password} />
                                <p className="text-[11px] text-amber-600 mt-1">User wajib ganti kata sandi saat login pertama.</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialog(false)}>Batal</Button>
                        <Button onClick={submit} disabled={saving} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="user-save-button">
                            {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!resetLinkFor} onOpenChange={(o) => !o && setResetLinkFor(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Kirim link reset kata sandi?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Link sekali-pakai akan dikirim ke <b>{resetLinkFor?.email}</b>, berlaku 60 menit.
                            Pemilik akun membuat kata sandi barunya sendiri lewat link itu -- Anda tidak akan
                            melihat kata sandi baru tersebut.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-send-reset-link"
                            disabled={resetLinkLoading}
                            className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                            onClick={sendResetLink}
                        >
                            {resetLinkLoading ? "Mengirim..." : "Kirim Link"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!resetSandiFor} onOpenChange={(o) => !o && setResetSandiFor(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset kata sandi {resetSandiFor?.email}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Kata sandi sementara akan dibuat dan ditampilkan SEKALI kepada Anda. Kata sandi lama
                            langsung tidak berlaku, semua sesi akun ini keluar, dan pemiliknya wajib mengganti kata
                            sandi saat login berikutnya. Sampaikan kata sandi sementara lewat jalur yang aman.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-reset-password"
                            disabled={resetSandiLoading}
                            className="bg-[#00B9AD] hover:bg-[#60C0D0]"
                            onClick={(e) => { e.preventDefault(); resetSandi(); }}
                        >
                            {resetSandiLoading ? "Memproses..." : "Reset Kata Sandi"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={!!sandiSementara} onOpenChange={(o) => !o && setSandiSementara(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Kata sandi sementara</DialogTitle></DialogHeader>
                    <p className="text-sm text-slate-600">Untuk akun <b>{sandiSementara?.email}</b>:</p>
                    <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
                        <code className="flex-1 font-mono text-base break-all text-slate-900" data-testid="temp-password-value">{sandiSementara?.sandi}</code>
                        <Button size="sm" variant="ghost" onClick={salinSandi} title="Salin"><Copy className="w-4 h-4" /></Button>
                    </div>
                    <p className="text-[12px] text-amber-700">
                        Kata sandi ini TIDAK akan ditampilkan lagi setelah dialog ditutup. Pemilik akun wajib menggantinya saat login.
                    </p>
                    <DialogFooter>
                        <Button onClick={() => setSandiSementara(null)} className="bg-[#00B9AD] hover:bg-[#60C0D0]" data-testid="temp-password-close">Selesai</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!resetTotpFor} onOpenChange={(o) => !o && setResetTotpFor(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset authenticator {resetTotpFor?.email}?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Authenticator (TOTP) & semua kode pemulihan akun ini akan dihapus. Pemiliknya WAJIB
                            mendaftarkan ulang authenticator (pindai QR baru) pada login berikutnya. Gunakan ini kalau
                            pemilik akun kehilangan ponsel/kode pemulihan.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="confirm-reset-totp"
                            disabled={resetTotpLoading}
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={resetTotp}
                        >
                            {resetTotpLoading ? "Memproses..." : "Reset Authenticator"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Hapus akun {confirmDel?.email}?</AlertDialogTitle>
                        <AlertDialogDescription>Tindakan ini tidak bisa dibatalkan.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={del}>Hapus</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <UbahEmailAkunDialog
                target={ubahEmailFor}
                onClose={() => setUbahEmailFor(null)}
                onDone={() => { setUbahEmailFor(null); load(); }}
            />

            <BulkCreateAdminDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                prodiList={prodi}
                existingUsers={users}
                onDone={load}
            />
        </Tabs>
    );
}
